import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { not } from 'tio-ui/utilities';
import Component from '@glimmer/component';
import NotificationHistoriesTable from '../notification-histories/table.gts';
import Chart from 'tio-reimagined/components/chart';
import type { ChartDataPoint } from '../../types/chart.ts';
import type EmployeeModel from '../../models/employee.ts';
import type Owner from '@ember/owner';
import type UserModel from '../../models/user.ts';

export interface ProfileNotificationHistoriesSignature {
  Args: {
    employee: EmployeeModel;
    user: UserModel;
    limit?: number;
  };
  Element: HTMLElement;
}

/**
 * ProfileNotificationHistoriesComponent - Uses native chart components
 *
 * This component displays notification history data as a donut chart via
 * tio-reimagined/components/chart. The chart renders declaratively with consistent
 * theming and responds to layout changes.
 *
 * Data flows:
 * 1. Uses the existing /users/{id}/aggregate API endpoint
 * 2. Groups by 'noteSubtype' field from notification_history table
 * 3. Renders as responsive donut chart with automatic dark mode support
 * 4. Supports filtering the table by clicking chart segments (future enhancement)
 */
export default class ProfileNotificationHistoriesComponent extends Component<ProfileNotificationHistoriesSignature> {
  @tracked noteSubtypeFilter = '';

  constructor(owner: Owner, args: ProfileNotificationHistoriesSignature['Args']) {
    super(owner, args);
    this.limit = this.args.limit || this.limit;
  }

  limit = 10;

  data = trackedFunction(this, async () => {
    const user = this.args.user;
    const data = await user.aggregate({
      model: 'notification-history',
      groupBy: 'noteSubtype',
      rollups: {
        value: 'count()',
      },
    });

    return data;
  });

  get chartData(): ChartDataPoint[] {
    const rawData = this.data?.value || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return rawData.map((item: any) => ({
      category: this.formatNotificationTypeName(item.name || item.category || 'Unknown'),
      value: item.value || item.count || 0,
    }));
  }

  get isLoading() {
    return this.data?.isLoading ?? true;
  }

  filterByNoteSubtype = (value: string) => {
    this.noteSubtypeFilter = value;
  };

  /**
   * Format notification type names for better readability
   */
  formatNotificationTypeName(name: string): string {
    // Keep the original name for translation lookup, but clean it up slightly
    return name
      .replace(/_NOTIFIER$/, '') // Remove trailing _NOTIFIER
      .replace(/_/g, ' ') // Replace underscores with spaces
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  }

  <template>
    <Section @collapsed={{true}}>
      <:header>{{t "common.email_history"}}</:header>
      <:body>
        <div class="flex flex-col lg:flex-row gap-6">
          {{#if @user}}
            <div class="lg:w-1/4 shrink-0">
              {{#if this.isLoading}}
                <div class="text-gray-500 text-center py-8">
                  <div class="text-sm">Loading chart...</div>
                </div>
              {{else if (not this.chartData.length)}}
                <div class="text-gray-500 text-center py-8">
                  <div class="text-sm">{{t "no_chart_data"}}</div>
                </div>
              {{else}}
                <Chart
                  @data={{this.chartData}}
                  @type="donut"
                  data-test-notification-history-chart
                  class="h-80"
                />
              {{/if}}
            </div>
          {{/if}}
          <div class="lg:flex-1 min-w-0 overflow-hidden">
            <div class="overflow-x-auto">
              <NotificationHistoriesTable
                @limit={{this.limit}}
                @employee={{@employee}}
                @noteSubtypeFilter={{this.noteSubtypeFilter}}
              />
            </div>
          </div>
        </div>
      </:body>
    </Section>
  </template>
}
